import React, { createContext, FC, useContext } from 'react';

import { MantineProvider, useMantineTheme } from '@mantine/core';

import { useIsMobile } from 'hooks/use-is-mobile';

import { DatasheetMode, useDatasheetMode } from './DatasheetMode';

const DatasheetReadOnlyContext = createContext(false);

const useDatasheetReadOnly = () => useContext(DatasheetReadOnlyContext);

const DatasheetMantineProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const mode = useDatasheetMode();
    const theme = useMantineTheme();
    const isMobile = useIsMobile('sm');

    const isViewMode = mode === DatasheetMode.VIEW;
    const valueFontSize = isViewMode && isMobile ? theme.fontSizes.sm : theme.fontSizes.md;

    return (
        <MantineProvider
            theme={{
                components: {
                    ...theme.components,
                    Autocomplete: {
                        defaultProps: {
                            readOnly: isViewMode,
                        },
                    },
                    Button: {
                        styles: {
                            root: {
                                pointerEvents: isViewMode ? 'none' : 'all',
                            },
                        },
                    },
                    Checkbox: {
                        defaultProps: {
                            disabled: isViewMode,
                        },
                    },
                    Input: {
                        defaultProps: {
                            readOnly: isViewMode,
                        },
                        styles: () => ({
                            input: {
                                background: 'transparent',
                                border: 'none',
                                fontSize: valueFontSize,
                                fontWeight: 400,
                            },
                            icon: {
                                color: 'currentColor',
                            },
                        }),
                    },
                    MultiSelect: {
                        defaultProps: {
                            readOnly: isViewMode,
                            size: 'md',
                        },
                        ...(isViewMode
                            ? {
                                  styles: {
                                      searchInput: {
                                          cursor: 'default !important',
                                      },
                                      input: {
                                          cursor: 'default',
                                      },
                                      section: {
                                          display: 'none',
                                      },
                                      pill: {
                                          fontSize: theme.fontSizes.md,
                                          fontWeight: 400,
                                      },
                                  },
                              }
                            : {
                                  pill: {
                                      fontSize: theme.fontSizes.md,
                                      fontWeight: 400,
                                  },
                              }),
                    },
                    NumberInput: {
                        defaultProps: {
                            hideControls: true,
                        },
                        styles: {
                            section: {
                                pointerEvents: isViewMode ? 'none' : 'all',
                                fontSize: theme.fontSizes.md,
                            },
                        },
                    },
                    Popover: {
                        defaultProps: {
                            styles: {
                                dropdown: {
                                    boxShadow: theme.shadows.md,
                                },
                            },
                        },
                    },
                    Select: {
                        defaultProps: {
                            readOnly: isViewMode,
                        },
                        ...(isViewMode
                            ? {
                                  styles: {
                                      input: {
                                          cursor: 'default !important',
                                      },
                                      section: {
                                          display: 'none',
                                      },
                                  },
                              }
                            : {}),
                    },
                    SegmentedControl: {
                        ...theme.components.SegmentedControl,
                        defaultProps: {
                            readOnly: isViewMode,
                            size: 'xs',
                        },
                    },
                    Table: {
                        styles: {
                            root: {
                                color: theme.colors.gray[7],
                                borderTop: `1px solid ${theme.colors.gray[3]}`,
                                borderBottom: `1px solid ${theme.colors.gray[3]}`,
                            },
                        },
                    },
                    Pill: {
                        styles: {
                            root: {
                                border: `1px solid ${theme.colors.gray[3]}`,
                                background: 'transparent',
                                fontSize: theme.fontSizes.md,
                                fontWeight: 400,
                            },
                        },
                    },
                    Badge: {
                        defaultProps: {
                            disabled: isViewMode,
                        },
                    },
                },
            }}
        >
            <DatasheetReadOnlyContext.Provider value={isViewMode}>{children}</DatasheetReadOnlyContext.Provider>
        </MantineProvider>
    );
};

export { DatasheetMantineProvider, DatasheetReadOnlyContext, useDatasheetReadOnly };
